import {Injectable} from '@angular/core';
import {
  ClientPrintJob,
  FileSourceType,
  InstalledPrinter,
  JSPrintManager,
  PrintFilePDF,
  UserSelectedPrinter, // UserSelectedPrinter import'unu koruyun
  WSStatus,
} from 'jsprintmanager';
import {UtilsService} from "@dabnet-kamu/libs/ui/shared/services/utils.service";
import {environment} from './app-settings.service';
import {QzTrayPrintService} from './qz-tray-print.service';


@Injectable({
  providedIn: 'root',
})
export class PrintFileService {
  private printerName: string;
  private printerType: 'W' | 'D';
  private fileContent: any;
  private silent: boolean = true; // Varsayılanı sessiz (silent) baskı olarak ayarla

  constructor(private utilService: UtilsService, private qzPrintService: QzTrayPrintService) {
  }

  /**
   * JSPM bağlantısının açılmasını bekler (Timeout ile).
   */
  private waitForJSPMConnect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (JSPrintManager.websocket_status === WSStatus.Open) return resolve();

      // Bağlantı kapalıysa başlatmayı dene
      if (JSPrintManager.websocket_status === WSStatus.Closed) JSPrintManager.start();

      const timeout = 5000; // Maksimum 5 saniye bekleme süresi
      const startTime = Date.now();

      const checkStatus = () => {
        const status = JSPrintManager.websocket_status;
        if (status === WSStatus.Open) resolve();
        else if (status === WSStatus.Blocked) reject(this.getJspmStatusError());
        else if (Date.now() - startTime > timeout) reject('JSPM bağlantı süresi doldu. Uygulama başlatılamadı.');
        else setTimeout(checkStatus, 500);
      };
      checkStatus();
    });
  }

  private getJspmStatusError(): string {
    const status = JSPrintManager.websocket_status;
    switch (status) {
      case WSStatus.Closed:
        return 'JSPrintManager (JSPM) istemci uygulaması yüklü değil veya çalışmıyor! Lütfen uygulamayı indirin ve çalıştırın: https://neodynamic.com/downloads/jspm';
      case WSStatus.Blocked:
        return 'JSPM bu web sitesini engelledi! Lütfen izin verilenler listesini kontrol edin.';
      default:
        return 'JSPM bağlantı sorunu: bilinmeyen durum.';
    }
  }

  /**
   * Baskı işini başlatır. Bağlantı kapalıysa, bu metot çağrılmadan önce beklenir.
   */

  print(
    printerName: string,
    fileContent: any,
    printerType: 'W' | 'D',
    silent: boolean = true,
    isDownload: boolean = false // 🆕 Yeni Parametre
  ): Promise<{ success: boolean; error: string }> {
    this.printerName = printerName;
    this.fileContent = fileContent;
    this.printerType = printerType;
    this.silent = silent;

    if (!this.fileContent) {
      this.utilService.showError('Dosya içeriği bulunamadı');
      return Promise.resolve({ success: false, error: 'Dosya içeriği eksik.' });
    }

    // 🆕 İNDİRME SENARYOSU
    if (isDownload) {
      return this.downloadFile(this.fileContent, this.printerType);
    }

    // YAZDIRMA SENARYOSU (Eski Akış)
    if (!this.printerName) {
      this.utilService.showError('Yazıcı bulunamadı');
      return Promise.resolve({ success: false, error: 'Yazıcı adı eksik.' });
    }

    // 🆕 JSPrintManager artık opsiyonel: sadece useJSPM=true ile açıkça istenirse kullanılır.
    if (environment.useJSPM) {
      // Önce bağlantıyı bekle, sonra baskıyı başlat
      return this.waitForJSPMConnect()
        .then(() => this.startPrint())
        .catch((errorMsg: string) => {
          this.utilService.showError(errorMsg);
          return { success: false, error: errorMsg };
        });
    }

    // Tüm müşteriler QZ Tray'e geçti: artık ana (varsayılan) akış.
    return this.qzPrintService
      .print(this.printerName, this.fileContent, this.printerType, this.silent)
      .then((result) => {
        if (!result.success) {
          this.utilService.showError(result.error);
        }
        return result;
      });
  }





  private startPrint(): Promise<{ success: boolean; error: string }> {
    return new Promise((resolve) => {
      // Yazıcı listesi alınırken hata oluşursa, bu genellikle bağlantı hatasıdır.
      JSPrintManager.getPrinters()
        .then((installedPrinters: any) => {
          const selectedPrinter = installedPrinters.find((p: any) => p === this.printerName);
          if (!selectedPrinter) {
            const errorMsg = `${this.printerName} yüklü değil veya JSPM erişemiyor.`;
            this.utilService.showError(errorMsg);
            return resolve({success: false, error: errorMsg});
          }

          const printPromise =
            this.printerType === 'W'
              ? this.doPrintPDF(selectedPrinter)
              : this.doPrintESCP(selectedPrinter);

          printPromise.then(resolve).catch((error) => {
            resolve({success: false, error: error?.message || 'Bilinmeyen hata'});
          });
        })
        .catch((error) => {
          resolve({success: false, error: error?.message || 'Yazıcı listesi alınamadı.'});
        });
    });
  }

  private doPrintESCP(printer: string): Promise<{ success: boolean; error: string }> {
    return new Promise((resolve) => {
      try {
        const cpj = new ClientPrintJob();
        cpj.clientPrinter = new InstalledPrinter(printer);
        cpj.printerCommands = this.fileContent;
        cpj.sendToClient()
          .then((result: any) => {
            if (!result.success) {
              // JSPM'den hata gelse bile konsola yazıp yoksayıyoruz.
              // Çünkü tahsilat başarılı ve işi yazıcıya yollamaya çalıştık.
              console.warn(`[JSPM Geri Bildirim Uyarısı] Tahsilat Makbuzu: ${result.error || 'Bilinmeyen Hata'}`);
            }
            // 🌟 Ana Değişiklik: Baskı işi gönderildi, success: true dön
            resolve({success: true, error: ''});
          })
          .catch((error) => {
            // Sadece gönderme hatası varsa (Socket/Ağ) false dönüyoruz
            resolve({success: false, error: error?.message || 'ESC/POS baskı hatası'});
          });
      } catch (e) {
        resolve({success: false, error: `ESC/POS hazırlık hatası: ${e}`});
      }
    });
  }

  // PrintFileService içindeki doPrintPDF metodunun güncellenmiş hali:
  private doPrintPDF(printer: string): Promise<{ success: boolean; error: string }> {
    return new Promise((resolve) => {
      try {
        const cpj = new ClientPrintJob();
        cpj.clientPrinter = this.silent ? new InstalledPrinter(printer) : new UserSelectedPrinter();
        const fileName = `${this.printerName}_${new Date().getMilliseconds()}.pdf`;
        const file = new PrintFilePDF(this.fileContent, FileSourceType.Base64, fileName, 1);
        cpj.files.push(file);

        cpj.sendToClient()
          .then((result: any) => {
            if (!result.success) {
              // JSPM'den hata gelse bile konsola yazıp yoksayıyoruz.
              console.warn(`[JSPM Geri Bildirim Uyarısı] Tahsilat Makbuzu (PDF): ${result.error || 'Bilinmeyen Hata'}`);
            }
            // 🌟 Ana Değişiklik: Baskı işi gönderildi, success: true dön
            resolve({success: true, error: ''});
          })
          .catch((error) => {
            // Sadece gönderme hatası varsa (Socket/Ağ) false dönüyoruz
            resolve({success: false, error: error?.message || 'PDF baskı hatası'});
          });
      } catch (e) {
        resolve({success: false, error: `PDF hazırlık hatası: ${e}`});
      }
    });
  }

  private base64ToString(base64: string): string {
    const cleanBase64 = base64.replace(/[^A-Za-z0-9+/=]/g, '');
    const binary = atob(cleanBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    // 'windows-1254' yaygın bir Türkçe kodlamadır.
    return new TextDecoder('windows-1254').decode(bytes);
  }

  /**
   * Ekran açılışında yazdırma bağlantısını erkenden başlatır.
   * useJSPM=true ile açıkça istenmedikçe QZ Tray'e bağlanır (karar burada, tek yerde verilir).
   */
  public initializePrinting(): void {
    if (environment.useJSPM) {
      // Otomatik yeniden bağlanmayı aç
      JSPrintManager.auto_reconnect = true;

      if (JSPrintManager.websocket_status === WSStatus.Closed) {
        console.log('JSPM bağlantısı ekran açılışında başlatılıyor...');
        JSPrintManager.start();
      }
      return;
    }

    this.qzPrintService.initialize();
  }


  private downloadFile(base64Content: string, type: 'W' | 'D'): Promise<{ success: boolean; error: string }> {
    return new Promise((resolve) => {
      try {
        // Base64 temizliği (Header varsa temizle, boşlukları sil)
        const cleanBase64 = base64Content.replace(/^data:.*;base64,/, '').replace(/\s/g, '');

        // Binary veriye çevirme
        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        // Dosya tipi ve uzantısını belirleme
        // 'W' ise PDF varsayıyoruz, 'D' ise Text varsayıyoruz (Nokta vuruşlu raw data)
        const blobType = type === 'W' ? 'application/pdf' : 'text/plain';
        const extension = type === 'W' ? 'pdf' : 'txt';

        const blob = new Blob([byteArray], { type: blobType });

        // İndirme linki oluşturma
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `Belge_${new Date().getTime()}.${extension}`;

        // Tıklama simülasyonu
        document.body.appendChild(anchor);
        anchor.click();

        // Temizlik
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);

        resolve({ success: true, error: '' });
      } catch (e) {
        console.error('İndirme hatası:', e);
        resolve({ success: false, error: 'Dosya indirme sırasında hata oluştu.' });
      }
    });
  }


}
